<template>
  
<FormKitLazyProvider>
<FormKit
    v-show="!success"
    id="account-create"
    name="account-create"
    type="form"
    :incomplete-message="$t('Sorry, not all fields are filled out correctly.')"
    :value="data"
    :actions="false"
    @submit="onSubmit"
  >
    <slot name="firstname">
      <FormKit
        type="text"
        :label="$t('First name')"
        name="firstname"
        validation="required"
      />
    </slot>
    <slot name="lastname">
      <FormKit
        type="text"
        :label="$t('Last name')"
        name="lastname"
        validation="required"
      />
    </slot>
    <slot name="is_subscribed">
      <FormKit
        type="checkbox"
        :label="$t('Subscribe to newsletter')"
        name="is_subscribed"
        :value="true"
      />
    </slot>
    <slot name="email">
      <FormKit
        id="account-create__email"
        type="email"
        :label="$t('Email address')"
        name="email"
        validation="required|email"
      />
    </slot>
    <slot name="password">
      <FormKit
        :type="showPassword ? 'text' : 'password'"
        :label="$t('Password')"
        name="password"
        outer-class="formkit-password"
        :validation="[['required'], ['length', 6], ['matches', '/^(?=.*[a-z])(?=.*[A-Z])|(?=.*[a-z])(?=.*\d)|(?=.*[A-Z])(?=.*\d)|(?=.*[a-z])(?=.*[^a-zA-Z\d])|(?=.*[A-Z])(?=.*[^a-zA-Z\d])|(?=.*\d)(?=.*[^a-zA-Z\d])/']]"
        :help="$t('Password must contain at least 2 different character types (lowercase, uppercase, numbers, or special characters) and a minimal length of 6 characters.')"
        :validation-messages="{
          required: $t('Password is required'),
          length: $t('Password must be at least 6 characters long'),
          matches: $t('Password must contain at least 2 different character types (lowercase, uppercase, numbers, or special characters)'),
        }"
      >
        <template #suffix>
          <button
            type="button"
            class="formkit-password-toggle"
            @click.prevent="showPassword = !showPassword"
          >
            <IconEyeOpen
              v-if="!showPassword"
              class="formkit-password-toggle__icon icon icon--eye-open"
            />
            <IconEyeClosed
              v-if="showPassword"
              class="formkit-password-toggle__icon icon icon--eye-closed"
            />
            <span class="formkit-password-toggle__label">{{ showPassword ? $t('Hide password') : $t('Show password') }}</span>
          </button>
        </template>
      </FormKit>
    </slot>
    <slot name="confirmPassword">
      <FormKit
        :type="showPassword2 ? 'text' : 'password'"
        :label="$t('Confirm password')"
        name="confirmPassword"
        outer-class="formkit-password"
        validation="required|confirm:password"
        validation-label="Password confirmation"
        :validation-messages="{
          required: $t('Password confirmation is required.'),
          confirm: $t('The passwords are not equal.'),
        }"
      >
        <template #suffix>
          <button
            type="button"
            class="formkit-password-toggle"
            @click.prevent="showPassword2 = !showPassword2"
          >
            <IconEyeOpen
              v-if="!showPassword2"
              class="formkit-password-toggle__icon icon icon--eye-open"
            />
            <IconEyeClosed
              v-if="showPassword2"
              class="formkit-password-toggle__icon icon icon--eye-closed"
            />
            <span class="formkit-password-toggle__label">{{ showPassword2 ? $t('Hide password') : $t('Show password') }}</span>
          </button>
        </template>
      </FormKit>
    </slot>

    <div class="formkit-buttons">
      <slot
        name="actions"
        v-bind="{ createCustomerLoading }"
      >
        <button
          :disabled="createCustomerLoading"
          type="submit"
          class="button"
          :class="{ 'button--loading': createCustomerLoading, 'button--cta': !travelbagsOnly, 'button--shop-flow button--full': travelbagsOnly }"
        >
          <span>{{ $t('Create account') }}</span>
          <IconLoader
            v-if="createCustomerLoading"
            class="icon icon--loader"
          />
          <IconArrow
            v-if="!travelbagsOnly"
            class="icon icon--right icon--arrow icon--arrow-right"
          />
        </button>
        <AppLink
          class="button button--link button--back"
          to="/account/"
        >
          <span>{{ $t('Back') }}</span>
        </AppLink>
      </slot>
    </div>
  </FormKit>
</FormKitLazyProvider>

</template>

<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
export interface Props {
  autoLogin?: boolean
}
const props = defineProps<Props>()

const emit = defineEmits(['success'])

const showPassword = ref<boolean>(false)
const showPassword2 = ref<boolean>(false)

const { public: config } = useRuntimeConfig()
const { show: showNotification } = useNotification()
const { createCustomer, createCustomerError, createCustomerLoading } = useAccountCustomer()
const { login } = useAccountLogin()
const { completeRegistration } = useDataLayer()
const data = ref<Parameters<typeof createCustomer>[0]>({
  firstname: '',
  lastname: '',
  is_subscribed: false,
  date_of_birth: '',
  email: '',
  password: '',
  taxvat: '',
})

const success = ref<boolean>(false)

const travelbagsOnly = computed(
  () => config.storeHeader.startsWith('tra'),
)

const onSubmit = async (formData: Parameters<typeof createCustomer>[0] & { confirmPassword: string }) => {
  const { type: _1, confirmPassword: _2, ...input } = formData
  await createCustomer(input)
  if (!createCustomerError.value) {
    completeRegistration(formData)
    if (props.autoLogin) {
      emit('success', true)
      await login({ email: formData.email, password: formData.password })
    }
  }
  else {
    showNotification({ type: 'error', message: useMessageHandler(createCustomerError.value?.message || '') })
  }
}
</script>
